/**
 * Turns plain educational text into text runs and LaTeX runs.
 * English stays text. A span becomes math only when it contains real notation
 * (a power, subscript, fraction, root, comparison, Greek name, degree, or similar).
 */

const GREEK = {
  pi: "\\pi",
  theta: "\\theta",
  alpha: "\\alpha",
  beta: "\\beta",
  gamma: "\\gamma",
  delta: "\\delta",
  omega: "\\omega",
  lambda: "\\lambda",
  mu: "\\mu",
  sigma: "\\sigma",
  phi: "\\phi",
  infinity: "\\infty",
  infty: "\\infty",
};

const FUNCTIONS = {
  sin: "sin",
  cos: "cos",
  tan: "tan",
  log: "log",
  ln: "ln",
};

const SYMBOLS = {
  "π": "\\pi",
  "θ": "\\theta",
  "α": "\\alpha",
  "β": "\\beta",
  "γ": "\\gamma",
  "δ": "\\delta",
  "ω": "\\omega",
  "λ": "\\lambda",
  "μ": "\\mu",
  "σ": "\\sigma",
  "φ": "\\phi",
  "∞": "\\infty",
};

const OPS = {
  plus: "+",
  minus: "-",
  times: "\\times",
  cdot: "\\cdot",
  div: "\\div",
  eq: "=",
  lt: "<",
  gt: ">",
  le: "\\leq",
  ge: "\\geq",
  ne: "\\neq",
  pm: "\\pm",
};

const isLetter = (char) => /[A-Za-z]/.test(char || "");
const isDigit = (char) => /[0-9]/.test(char || "");
const isMinusChar = (char) => char === "-" || char === "−" || char === "–";

/** Unicode superscripts, recognized by block rather than by listing each glyph. */
const isSuperscriptChar = (char) => {
  if (!char) return false;
  const code = char.codePointAt(0);
  const inBlock = code === 0x00b2 || code === 0x00b3 || code === 0x00b9 || (code >= 0x2070 && code <= 0x207f);
  return inBlock && char.normalize("NFKD") !== char;
};

/** Unicode subscripts, recognized by block rather than by listing each glyph. */
const isSubscriptChar = (char) => {
  if (!char) return false;
  const code = char.codePointAt(0);
  return code >= 0x2080 && code <= 0x209f && char.normalize("NFKD") !== char;
};

const readScriptRun = (parser, test) => {
  let value = "";
  while (parser.i < parser.n && test(parser.src[parser.i])) {
    value += parser.src[parser.i].normalize("NFKD");
    parser.i += 1;
  }
  return value;
};

const snap = (parser) => ({ i: parser.i, signals: parser.signals });
const restore = (parser, saved) => {
  parser.i = saved.i;
  parser.signals = saved.signals;
};

const skipSpace = (parser) => {
  const start = parser.i;
  while (parser.src[parser.i] === " " || parser.src[parser.i] === "\t") parser.i += 1;
  return parser.i > start;
};

const peekWord = (parser) => {
  const match = /^[A-Za-z]+/.exec(parser.src.slice(parser.i));
  return match ? match[0] : "";
};

const wordBoundary = (parser, length) => !isLetter(parser.src[parser.i + length]);

const emit = (node) => {
  if (!node) return "";
  switch (node.type) {
    case "num":
      return node.value;
    case "var":
      return `${node.name}${node.sub ? `_{${node.sub}}` : ""}`;
    case "sym":
      return node.latex;
    case "sup":
      return `{${emit(node.base)}}^{${emit(node.exp)}}`;
    case "frac":
      return `\\frac{${emit(node.left)}}{${emit(node.right)}}`;
    case "binop":
      return `${emit(node.left)} ${OPS[node.op] || node.op} ${emit(node.right)}`;
    case "juxt":
      return node.parts.map(emit).join(" ");
    case "neg":
      return `-${emit(node.body)}`;
    case "pm":
      return `\\pm ${emit(node.body)}`;
    case "abs":
      return `\\left|${emit(node.body)}\\right|`;
    case "sqrt":
      return `\\sqrt{${emit(node.body)}}`;
    case "func":
      return `\\${node.name}\\left(${emit(node.body)}\\right)`;
    case "group":
      return `\\left(${emit(node.body)}\\right)`;
    case "deg":
      return `{${emit(node.body)}}^{\\circ}`;
    default:
      return "";
  }
};

const readDelimited = (src, index) => {
  const openers = [
    { start: "$$", end: "$$", display: true },
    { start: "\\[", end: "\\]", display: true },
    { start: "\\(", end: "\\)", display: false },
  ];
  for (const opener of openers) {
    if (!src.startsWith(opener.start, index)) continue;
    const close = src.indexOf(opener.end, index + opener.start.length);
    if (close < 0) return null;
    const latex = src.slice(index + opener.start.length, close).trim();
    if (!latex) return null;
    return { latex, end: close + opener.end.length, display: opener.display };
  }
  if (src[index] !== "$" || src[index + 1] === "$") return null;
  const close = src.indexOf("$", index + 1);
  if (close < 0) return null;
  const latex = src.slice(index + 1, close);
  if (!latex.trim() || /[\n\r]/.test(latex)) return null;
  if (!/[\\^_=<>{}]/.test(latex)) return null;
  return { latex: latex.trim(), end: close + 1, display: false };
};

const readSubscript = (parser) => {
  if (parser.src[parser.i] !== "_") return "";
  const saved = snap(parser);
  parser.i += 1;
  if (parser.src[parser.i] === "{") {
    const body = parseBrace(parser);
    if (!body) {
      restore(parser, saved);
      return "";
    }
    parser.signals += 1;
    return emit(body);
  }
  const digits = /^[0-9]+/.exec(parser.src.slice(parser.i));
  if (digits) {
    parser.i += digits[0].length;
    parser.signals += 1;
    return digits[0];
  }
  if (isLetter(parser.src[parser.i]) && !isLetter(parser.src[parser.i + 1])) {
    const letter = parser.src[parser.i];
    parser.i += 1;
    parser.signals += 1;
    return letter;
  }
  restore(parser, saved);
  return "";
};

const takeDegree = (parser, node) => {
  const saved = snap(parser);
  skipSpace(parser);
  if (parser.src[parser.i] === "°") {
    parser.i += 1;
    parser.signals += 1;
    return { type: "deg", body: node };
  }
  const word = peekWord(parser);
  if (/^degrees?$/i.test(word) && wordBoundary(parser, word.length)) {
    parser.i += word.length;
    parser.signals += 1;
    return { type: "deg", body: node };
  }
  restore(parser, saved);
  return node;
};

const parseBrace = (parser) => {
  if (parser.src[parser.i] !== "{") return null;
  const saved = snap(parser);
  parser.i += 1;
  const body = parseComparison(parser);
  if (!body || parser.src[parser.i] !== "}") {
    restore(parser, saved);
    return null;
  }
  parser.i += 1;
  return body;
};

const parseGroup = (parser, open, close, type) => {
  if (parser.src[parser.i] !== open) return null;
  const saved = snap(parser);
  parser.i += 1;
  const body = parseComparison(parser);
  if (!body || parser.src[parser.i] !== close) {
    restore(parser, saved);
    return null;
  }
  parser.i += 1;
  if (type === "abs") parser.signals += 1;
  return { type, body };
};

const parsePrimary = (parser) => {
  const src = parser.src;
  const char = src[parser.i];
  if (!char || char === "\n" || char === "\r") return null;

  if (char === "|") return parseGroup(parser, "|", "|", "abs");
  if (char === "(") return parseGroup(parser, "(", ")", "group");

  if (char === "√") {
    const saved = snap(parser);
    parser.i += 1;
    const body = src[parser.i] === "(" ? parseGroup(parser, "(", ")", "group") : parsePrimary(parser);
    if (!body) {
      restore(parser, saved);
      return null;
    }
    parser.signals += 1;
    return { type: "sqrt", body: body.type === "group" ? body.body : body };
  }

  if (SYMBOLS[char]) {
    parser.i += 1;
    parser.signals += 1;
    return { type: "sym", latex: SYMBOLS[char] };
  }

  const number = /^(\d+(?:\.\d+)?)/.exec(src.slice(parser.i));
  if (number && (parser.i === 0 || !isLetter(src[parser.i - 1]))) {
    parser.i += number[0].length;
    return takeDegree(parser, { type: "num", value: number[0] });
  }

  const word = peekWord(parser);
  if (!word || !wordBoundary(parser, word.length)) return null;
  const lower = word.toLowerCase();

  if (lower === "sqrt" || lower === "abs" || FUNCTIONS[lower]) {
    const saved = snap(parser);
    parser.i += word.length;
    skipSpace(parser);
    const group = parseGroup(parser, "(", ")", "group");
    if (!group) {
      restore(parser, saved);
      return null;
    }
    parser.signals += 1;
    if (lower === "sqrt") return { type: "sqrt", body: group.body };
    if (lower === "abs") return { type: "abs", body: group.body };
    return { type: "func", name: FUNCTIONS[lower], body: group.body };
  }

  if (GREEK[lower]) {
    parser.i += word.length;
    parser.signals += 1;
    return { type: "sym", latex: GREEK[lower] };
  }

  if (word.length === 1) {
    parser.i += 1;
    const asciiSub = readSubscript(parser);
    const unicodeSub = readScriptRun(parser, isSubscriptChar);
    if (unicodeSub) parser.signals += 1;
    const sub = `${asciiSub}${unicodeSub}`;
    return { type: "var", name: word, sub };
  }

  return null;
};

const attachPower = (parser, base) => {
  if (!base) return base;
  const unicodeExp = readScriptRun(parser, isSuperscriptChar);
  if (unicodeExp) {
    parser.signals += 1;
    base = { type: "sup", base, exp: { type: "num", value: unicodeExp } };
  }
  if (parser.src[parser.i] !== "^") return base;
  const saved = snap(parser);
  parser.i += 1;
  const exp = parser.src[parser.i] === "{" ? parseBrace(parser) : parseUnary(parser);
  if (!exp) {
    restore(parser, saved);
    return base;
  }
  parser.signals += 1;
  return { type: "sup", base, exp };
};

const readUnarySign = (parser) => {
  const src = parser.src;
  if (src.startsWith("+/-", parser.i)) {
    parser.i += 3;
    return "pm";
  }
  if (src.startsWith("+-", parser.i)) {
    parser.i += 2;
    return "pm";
  }
  if (src[parser.i] === "±") {
    parser.i += 1;
    return "pm";
  }
  if (isMinusChar(src[parser.i]) || src[parser.i] === "+") {
    const sign = src[parser.i] === "+" ? "plus" : "minus";
    parser.i += 1;
    return sign;
  }
  return "";
};

const parseUnary = (parser) => {
  const sign = readUnarySign(parser);
  if (!sign) return parsePrimary(parser);
  const body = parseUnary(parser);
  if (!body) return null;
  parser.signals += 1;
  if (sign === "pm") return { type: "pm", body };
  if (sign === "plus") return body;
  return { type: "neg", body };
};

const parsePower = (parser) => attachPower(parser, parseUnary(parser));

const canStartPrimary = (parser) => {
  const char = parser.src[parser.i];
  if (!char || char === "\n" || char === "\r") return false;
  if ("(|√".includes(char) || SYMBOLS[char]) return true;
  if (isDigit(char)) return true;
  return Boolean(peekWord(parser));
};

const isOperatorStart = (parser) => {
  const src = parser.src;
  const char = src[parser.i];
  if (!char) return false;
  if (src.startsWith("+/-", parser.i) || src.startsWith("<=", parser.i) || src.startsWith(">=", parser.i) || src.startsWith("!=", parser.i)) {
    return true;
  }
  return isMinusChar(char) || "+±*/×÷·^<>=≤≥≠".includes(char);
};

const parseImplicit = (parser) => {
  const first = parsePower(parser);
  if (!first) return null;
  const parts = [first];
  while (parser.i < parser.n) {
    const saved = snap(parser);
    const spaced = skipSpace(parser);
    if (!canStartPrimary(parser) || isOperatorStart(parser)) {
      restore(parser, saved);
      break;
    }
    if (spaced) {
      const word = peekWord(parser);
      const symbol = SYMBOLS[parser.src[parser.i]];
      const singleLetter = word.length === 1 && wordBoundary(parser, 1);
      const greekWord = Boolean(word && GREEK[word.toLowerCase()]);
      if (!symbol && !greekWord && !(singleLetter && parser.signals > 0)) {
        restore(parser, saved);
        break;
      }
    }
    const next = parsePower(parser);
    if (!next) {
      restore(parser, saved);
      break;
    }
    parts.push(next);
  }
  return parts.length === 1 ? first : { type: "juxt", parts };
};

const readMulOp = (parser) => {
  const src = parser.src;
  const char = src[parser.i];
  if (char === "*" && src[parser.i + 1] !== "*") {
    parser.i += 1;
    return "times";
  }
  if (char === "×") {
    parser.i += 1;
    return "times";
  }
  if (char === "·") {
    parser.i += 1;
    return "cdot";
  }
  if (char === "÷") {
    parser.i += 1;
    return "div";
  }
  if (char === "/" && src[parser.i + 1] !== "/") {
    parser.i += 1;
    return "/";
  }
  if (char === "x" && /\s/.test(src[parser.i - 1] || "") && /\s/.test(src[parser.i + 1] || "")) {
    const ahead = src.slice(parser.i + 1);
    if (/^\s+(?:\d|\()/.test(ahead)) {
      parser.i += 1;
      return "times";
    }
  }
  return "";
};

const parseMultiplicative = (parser) => {
  let left = parseImplicit(parser);
  if (!left) return null;
  while (parser.i < parser.n) {
    const saved = snap(parser);
    skipSpace(parser);
    const op = readMulOp(parser);
    if (!op) {
      restore(parser, saved);
      break;
    }
    skipSpace(parser);
    const right = parseImplicit(parser);
    if (!right) {
      restore(parser, saved);
      break;
    }
    parser.signals += 1;
    left = op === "/" ? { type: "frac", left, right } : { type: "binop", op, left, right };
  }
  return left;
};

const readAddOp = (parser) => {
  const src = parser.src;
  if (src.startsWith("+/-", parser.i)) {
    parser.i += 3;
    return "pm";
  }
  if (src[parser.i] === "±") {
    parser.i += 1;
    return "pm";
  }
  if (src[parser.i] === "+") {
    parser.i += 1;
    return "plus";
  }
  if (isMinusChar(src[parser.i])) {
    parser.i += 1;
    return "minus";
  }
  return "";
};

const parseAdditive = (parser) => {
  let left = parseMultiplicative(parser);
  if (!left) return null;
  while (parser.i < parser.n) {
    const saved = snap(parser);
    skipSpace(parser);
    const op = readAddOp(parser);
    if (!op) {
      restore(parser, saved);
      break;
    }
    skipSpace(parser);
    const right = parseMultiplicative(parser);
    if (!right) {
      restore(parser, saved);
      break;
    }
    parser.signals += 1;
    left = { type: "binop", op, left, right };
  }
  return left;
};

const readCmp = (parser) => {
  const src = parser.src;
  const pairs = [
    ["<=", "le"],
    [">=", "ge"],
    ["!=", "ne"],
    ["≤", "le"],
    ["≥", "ge"],
    ["≠", "ne"],
    ["=", "eq"],
    ["<", "lt"],
    [">", "gt"],
  ];
  for (const [token, op] of pairs) {
    if (src.startsWith(token, parser.i)) {
      parser.i += token.length;
      return op;
    }
  }
  return "";
};

const parseComparison = (parser) => {
  let left = parseAdditive(parser);
  if (!left) return null;
  while (parser.i < parser.n) {
    const saved = snap(parser);
    skipSpace(parser);
    const op = readCmp(parser);
    if (!op) {
      restore(parser, saved);
      break;
    }
    skipSpace(parser);
    const right = parseAdditive(parser);
    if (!right) {
      restore(parser, saved);
      break;
    }
    parser.signals += 1;
    left = { type: "binop", op, left, right };
  }
  return left;
};

const tryExpression = (src, start) => {
  if (start > 0 && /[A-Za-z0-9_]/.test(src[start - 1])) return null;
  const parser = { src, i: start, n: src.length, signals: 0 };
  const node = parseComparison(parser);
  if (!node || parser.signals <= 0 || parser.i === start) return null;
  return { latex: emit(node), end: parser.i, display: false };
};

const isDate = (src, index) => /^\d{1,2}\/\d{1,2}\/\d{2,4}(?!\d)/.test(src.slice(index));

/**
 * @returns {{type:"text", value:string}|{type:"math", latex:string, display:boolean}}[]
 */
export const segmentMath = (input) => {
  const src = String(input ?? "");
  const parts = [];
  let text = "";
  let index = 0;
  const flush = () => {
    if (!text) return;
    parts.push({ type: "text", value: text });
    text = "";
  };

  while (index < src.length) {
    if (/^https?:\/\//i.test(src.slice(index))) {
      const url = /^https?:\/\/\S+/.exec(src.slice(index));
      text += url[0];
      index += url[0].length;
      continue;
    }
    if (isDate(src, index)) {
      const date = /^\d{1,2}\/\d{1,2}\/\d{2,4}/.exec(src.slice(index));
      text += date[0];
      index += date[0].length;
      continue;
    }
    const delimited = readDelimited(src, index);
    if (delimited) {
      flush();
      parts.push({ type: "math", latex: delimited.latex, display: delimited.display });
      index = delimited.end;
      continue;
    }
    const parsed = tryExpression(src, index);
    if (parsed) {
      flush();
      parts.push({ type: "math", latex: parsed.latex, display: false });
      index = parsed.end;
      continue;
    }
    text += src[index];
    index += 1;
  }
  flush();
  return parts;
};

export const mathLatex = (input) =>
  segmentMath(input)
    .filter((part) => part.type === "math")
    .map((part) => part.latex);
